import { get, orderBy } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';

import { PROP_KEYS } from './sheets';

const DEFAULT_CATEGORY = 'none value';

const createDataRow = (
  storeName,
  formattedStoreGroups,
  productName,
  categoryName,
  subCategory,
  dataSource,
  categoryRatio,
) => ({
  [PROP_KEYS.STORE_NAME]: storeName,
  [PROP_KEYS.GROUPS]: formattedStoreGroups,
  [PROP_KEYS.PRODUCT_NAME]: productName,
  [PROP_KEYS.CATEGORY]: categoryName,
  [PROP_KEYS.SUB_CATEGORY]: subCategory,
  [PROP_KEYS.SOLD_QUANTITY]: get(dataSource, 'stats.unit', null),
  [PROP_KEYS.AVERAGE_PRICE]: get(dataSource, 'stats.priceExcludingTaxes', null),
  [PROP_KEYS.TURNOVER]: get(dataSource, 'stats.turnoverExcludingTaxes', null),
  [PROP_KEYS.CATEGORY_RATIO]: categoryRatio,
  [PROP_KEYS.MIX_RATIO]: get(dataSource, 'stats.mixExcludingTaxes', null),
  [PROP_KEYS.FOOD_COST_PER_UNIT]: get(dataSource, 'stats.foodCostPerUnit', null),
  [PROP_KEYS.FOOD_COST_TOTAL]: get(dataSource, 'stats.foodCostTotal', null),
  [PROP_KEYS.MARGIN]: get(dataSource, 'stats.margin', null),
  [PROP_KEYS.MARGIN_RATIO]: get(dataSource, 'stats.marginRatio', null),
  [PROP_KEYS.INVENTORY_SHRINKAGE]: get(dataSource, 'stats.inventoryShrinkage', null),
  [PROP_KEYS.PRODUCT_LOSSES]: get(dataSource, 'stats.productLosses', null),
  [PROP_KEYS.INGREDIENT_LOSSES]: get(dataSource, 'stats.ingredientLosses', null),
  [PROP_KEYS.ADJUSTED_THEORETICAL_MARGIN]: get(dataSource, 'stats.adjustedTheoreticalMargin', null),
});

const extractByStoreData = (store, groupsByStoreId, { productsByCategory }) => {
  const storeGroups = groupsByStoreId[store.id] || [];
  const formattedStoreGroups = storeGroups.join(', ');
  const storeData = [];

  orderBy(
    Object.entries(productsByCategory),
    [
      (entry) => get(entry[1], 'stats.mix', 0),
      (entry) => get(entry[1], 'stats.unit', 0),
      (entry) => entry[0],
    ],
    ['desc', 'desc', 'asc'],
  ).forEach(([category, { nestedData, stats }]) => {
    const categoryName = category === DEFAULT_CATEGORY ? i18next.t('GENERAL.NONE_VALUE') : category;

    orderBy(
      nestedData || [],
      [(item) => get(item, 'stats.mix', 0), (item) => get(item, 'stats.unit', 0), 'name'],
      ['desc', 'desc', 'asc'],
    ).forEach((productData) => {
      const subCategory = get(productData, 'subCategory', null) || i18next.t('GENERAL.NONE_VALUE');

      storeData.push(
        createDataRow(
          store.name,
          formattedStoreGroups,
          productData.name,
          categoryName,
          subCategory,
          productData,
          stats.mixExcludingTaxes || null,
        ),
      );

      // Add modifiers if any
      orderBy(
        productData.nestedData || [],
        [(item) => get(item, 'stats.mix', 0), (item) => get(item, 'stats.unit', 0), 'name'],
        ['desc', 'desc', 'asc'],
      ).forEach((modifierData) => {
        storeData.push(
          createDataRow(
            store.name,
            formattedStoreGroups,
            ' ├ ' + modifierData.name,
            categoryName,
            subCategory,
            modifierData,
            stats.mixExcludingTaxes || null,
          ),
        );
      });
    });
  });

  return storeData;
};

const extractContextData = (store, context, { turnoverExcludingTaxes, margin }) => {
  const startDate = moment(context.startDate).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR);
  const endDate = moment(context.endDate).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR);

  return {
    [PROP_KEYS.STORE_NAME]: store.name,
    [PROP_KEYS.START_DATE]: startDate,
    [PROP_KEYS.END_DATE]: endDate,
    [PROP_KEYS.TURNOVER]: turnoverExcludingTaxes,
    [PROP_KEYS.MARGIN]: margin.total,
    [PROP_KEYS.MARGIN_RATIO]: margin.ratioToTurnover,
  };
};

const getDetailedSheetData = () => [
  {
    [PROP_KEYS.SOLD_QUANTITY]: i18next.t('ANALYSIS.SALES.PAST_MIX_EXPORT_BY_STORE_COLUMN_SOLD_QTY'),
    [PROP_KEYS.AVERAGE_PRICE]: i18next.t(
      'ANALYSIS.SALES.PAST_MIX_EXPORT_BY_STORE_COLUMN_AVERAGE_PRICE',
    ),
    [PROP_KEYS.TURNOVER]: i18next.t(
      'ANALYSIS.SALES.PAST_MIX_EXPORT_BY_STORE_COLUMN_TURNOVER_EX_TAX',
    ),
    [PROP_KEYS.CATEGORY_RATIO]: i18next.t(
      'ANALYSIS.SALES.PAST_MIX_EXPORT_BY_STORE_COLUMN_CATEGORY_RATIO',
    ),
    [PROP_KEYS.MIX_RATIO]: i18next.t('ANALYSIS.SALES.PAST_MIX_EXPORT_BY_STORE_COLUMN_MIX_RATIO'),
    [PROP_KEYS.FOOD_COST_PER_UNIT]: i18next.t(
      'ANALYSIS.SALES.PAST_MIX_EXPORT_BY_STORE_COLUMN_FOOD_COST_UNIT',
    ),
    [PROP_KEYS.FOOD_COST_TOTAL]: i18next.t(
      'ANALYSIS.SALES.PAST_MIX_EXPORT_BY_STORE_COLUMN_FOOD_COST_TOTAL',
    ),
    [PROP_KEYS.MARGIN]: i18next.t('ANALYSIS.SALES.PAST_MIX_EXPORT_BY_STORE_COLUMN_MARGIN'),
    [PROP_KEYS.MARGIN_RATIO]: i18next.t(
      'ANALYSIS.SALES.PAST_MIX_EXPORT_BY_STORE_COLUMN_MARGIN_RATIO',
    ),
    [PROP_KEYS.INVENTORY_SHRINKAGE]: i18next.t(
      'ANALYSIS.SALES.PAST_MIX_EXPORT_BY_STORE_COLUMN_INVENTORY_SHRINKAGE',
    ),
    [PROP_KEYS.PRODUCT_LOSSES]: i18next.t(
      'ANALYSIS.SALES.PAST_MIX_EXPORT_BY_STORE_COLUMN_PRODUCT_LOSSES',
    ),
    [PROP_KEYS.INGREDIENT_LOSSES]: i18next.t(
      'ANALYSIS.SALES.PAST_MIX_EXPORT_BY_STORE_COLUMN_INGREDIENT_LOSSES',
    ),
    [PROP_KEYS.ADJUSTED_THEORETICAL_MARGIN]: i18next.t(
      'ANALYSIS.SALES.PAST_MIX_EXPORT_BY_STORE_COLUMN_ADJUSTED_THEORETICAL_MARGIN',
    ),
  },
];

export default {
  extractByStoreData,
  extractContextData,
  getDetailedSheetData,
};
